import React, { Component } from "react";
import { toast } from "react-toastify";
import "./productMediaActions.css";

const getExtension = (url, fallback) => {
  try {
    const clean = String(url).split("?")[0].split("#")[0];
    const last = clean.substring(clean.lastIndexOf("/") + 1);
    const dot = last.lastIndexOf(".");
    if (dot > -1) {
      const ext = last.substring(dot + 1).toLowerCase();
      if (ext.length >= 2 && ext.length <= 5) {
        return ext;
      }
    }
  } catch (e) {}
  return fallback;
};

const formatBytes = (bytes) => {
  if (!bytes || bytes < 1024) {
    return `${bytes || 0} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Marketplace links sometimes come as http:// or protocol-less //, which an
// https page cannot fetch.
const cleanUrl = (url) =>
  String(url || "")
    .trim()
    .replace(/^\/\//, "https://")
    .replace(/^http:\/\//i, "https://");

// Phones and in-app browsers (iOS Safari, Facebook, Messenger…) ignore
// <a download>; there the system share sheet's "Save image" works instead.
const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0) &&
  window.matchMedia &&
  window.matchMedia("(pointer: coarse)").matches;

const shareFile = async (blob, fileName) => {
  if (!isTouchDevice() || !navigator.share || !navigator.canShare || typeof File === "undefined") {
    return false;
  }
  try {
    const file = new File([blob], fileName, { type: blob.type || "application/octet-stream" });
    if (!navigator.canShare({ files: [file] })) {
      return false;
    }
    await navigator.share({ files: [file] });
    return true;
  } catch (error) {
    // Cancelling the sheet counts as handled; anything else falls back to a download.
    return error && error.name === "AbortError";
  }
};

// Opening a tab after an await is blocked as a popup, so the fallback is a
// plain link click, which browsers allow.
const openDirect = (url, fileName) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const saveBlob = (blob, fileName) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 2000);
};

class ProductMediaActions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      // { [index]: { percent: number | null, loaded: number, total: number } }
      downloading: {},
    };
  }

  getMediaList = () => {
    const { item } = this.props;
    const media = [];
    if (item && item.pictures && item.pictures.length > 0) {
      item.pictures.forEach((picture) => {
        if (picture) {
          media.push({ type: "image", url: picture, thumb: picture });
        }
      });
    }
    if (item && item.videos && item.videos.length > 0) {
      item.videos.forEach((video) => {
        if (video && video.Url) {
          media.push({
            type: "video",
            url: video.Url,
            thumb:
              video.PreviewUrl ||
              (item.pictures && item.pictures.length > 0
                ? item.pictures[0]
                : ""),
          });
        }
      });
    }
    return media;
  };

  onOpenModal = () => {
    this.setState({ open: true });
  };

  onCloseModal = () => {
    this.setState({ open: false });
  };

  copyLink = async () => {
    const link = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const input = document.createElement("textarea");
        input.value = link;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      toast.success("Link copied");
    } catch (error) {
      console.error("Could not copy link:", error);
      toast.error("Could not copy the link");
    }
  };

  shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      window.location.href,
    )}`;
    window.open(url, "_blank", "noopener,noreferrer,width=640,height=640");
  };

  shareOnWhatsapp = () => {
    const { item } = this.props;
    const text = `${item && item.name ? item.name : "Check this product"}\n${
      window.location.href
    }`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  setProgress = (index, progress) => {
    this.setState((prevState) => ({
      downloading: { ...prevState.downloading, [index]: progress },
    }));
  };

  clearProgress = (index) => {
    this.setState((prevState) => {
      const downloading = { ...prevState.downloading };
      delete downloading[index];
      return { downloading };
    });
  };

  // Streams the response so a big file (a product video especially) can report
  // a real percentage instead of sitting on a spinner.
  readWithProgress = async (response, index) => {
    const total = parseInt(response.headers.get("content-length"), 10);
    if (!response.body || !response.body.getReader) {
      return response.blob();
    }

    const reader = response.body.getReader();
    const chunks = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      chunks.push(value);
      loaded += value.length;
      this.setProgress(index, {
        loaded,
        total: total || 0,
        percent: total
          ? Math.min(100, Math.round((loaded / total) * 100))
          : null,
      });
    }

    return new Blob(chunks, {
      type: response.headers.get("content-type") || "application/octet-stream",
    });
  };

  downloadMedia = async (media, index) => {
    if (this.state.downloading[index]) {
      return;
    }
    const { item } = this.props;
    const baseName = item && item.id ? item.id : "product";
    const extension = getExtension(
      media.url,
      media.type === "video" ? "mp4" : "jpg",
    );
    const fileName = `${baseName}-${index + 1}.${extension}`;
    const url = cleanUrl(media.url);

    this.setProgress(index, { loaded: 0, total: 0, percent: 0 });

    try {
      const response = await fetch(url, {
        mode: "cors",
        credentials: "omit",
        referrerPolicy: "no-referrer",
      });
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      const blob = await this.readWithProgress(response, index);
      const shared = await shareFile(blob, fileName);
      if (!shared) {
        saveBlob(blob, fileName);
      }
      toast.success(shared ? "Ready to save" : "Download started");
    } catch (error) {
      console.error("Direct download failed, opening the file instead:", error);
      openDirect(url, fileName);
      toast.info("Opened the file — long-press or use the menu to save it");
    }

    this.clearProgress(index);
  };

  downloadAll = async () => {
    const media = this.getMediaList();
    for (let index = 0; index < media.length; index++) {
      // One after another, so the browser doesn't drop simultaneous downloads.
      // eslint-disable-next-line no-await-in-loop
      await this.downloadMedia(media[index], index);
    }
  };

  render() {
    const media = this.getMediaList();
    const { downloading, open } = this.state;

    return (
      <div className="product-media-actions">
        <button
          type="button"
          className="pma-icon-btn"
          title="Copy link"
          onClick={this.copyLink}
        >
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              fill="none"
              stroke="#8a8f98"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10 13.5a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1.2 1.2M14 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1.2-1.2"
            />
          </svg>
        </button>

        <button
          type="button"
          className="pma-icon-btn"
          title="Share on Facebook"
          onClick={this.shareOnFacebook}
        >
          <svg viewBox="0 0 24 24" width="22" height="22">
            <circle cx="12" cy="12" r="11" fill="#1877F2" />
            <path
              fill="#ffffff"
              d="M15.1 12.6l.4-2.6h-2.5V8.3c0-.7.3-1.4 1.5-1.4h1.1V4.7s-1-.2-2-.2c-2 0-3.3 1.2-3.3 3.4V10H8v2.6h2.3V19h2.7v-6.4h2.1z"
            />
          </svg>
        </button>

        <button
          type="button"
          className="pma-icon-btn"
          title="Share on WhatsApp"
          onClick={this.shareOnWhatsapp}
        >
          <svg viewBox="0 0 24 24" width="22" height="22">
            <circle cx="12" cy="12" r="11" fill="#25D366" />
            <path
              fill="#ffffff"
              d="M16.3 13.9c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.5.1-.2.2-.6.8-.7.9-.1.2-.3.2-.5.1-.2-.1-1-.4-1.9-1.2-.7-.6-1.2-1.4-1.3-1.6-.1-.2 0-.4.1-.5l.4-.4c.1-.2.2-.3.2-.4.1-.2 0-.3 0-.4 0-.1-.5-1.3-.7-1.7-.2-.4-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.2.2-.8.8-.8 2s.8 2.3.9 2.4c.1.2 1.6 2.6 4 3.5.6.2 1 .4 1.3.5.6.2 1.1.2 1.5.1.5-.1 1.4-.6 1.6-1.1.2-.6.2-1 .1-1.1 0-.2-.2-.2-.5-.3z"
            />
            <path
              fill="#ffffff"
              d="M12 5.5c-3.6 0-6.5 2.9-6.5 6.5 0 1.2.3 2.3.9 3.2l-1 3.3 3.4-1c.9.5 1.9.8 3.2.8 3.6 0 6.5-2.9 6.5-6.5S15.6 5.5 12 5.5zm0 11.9c-1.1 0-2.1-.3-3-.8l-.2-.1-2 .6.6-2-.1-.2c-.6-.9-.9-1.9-.9-3 0-3.1 2.5-5.5 5.6-5.5s5.5 2.5 5.5 5.5-2.4 5.5-5.5 5.5z"
            />
          </svg>
        </button>

        <button
          type="button"
          className="pma-icon-btn"
          title="Download images & videos"
          onClick={this.onOpenModal}
        >
          <svg viewBox="0 0 24 24" width="19" height="19">
            <path
              fill="none"
              stroke="#8a8f98"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 3.5v10.5m0 0l-3.8-3.8M12 14l3.8-3.8M4.5 16.5v2a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-2"
            />
          </svg>
        </button>

        {open && (
          <div className="pma-modal-overlay" onClick={this.onCloseModal}>
            <div
              className="pma-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="pma-modal-header">
                <h4>Download Images &amp; Videos</h4>
                <button
                  type="button"
                  className="pma-modal-close"
                  onClick={this.onCloseModal}
                >
                  &times;
                </button>
              </div>
              <p className="pma-modal-subtitle">
                Tap any item to save it &mdash; {media.length}{" "}
                {media.length === 1 ? "file" : "files"} available
              </p>
              {media.length > 1 ? (
                <button
                  type="button"
                  className="pma-download-all"
                  onClick={this.downloadAll}
                  disabled={Object.keys(downloading).length > 0}
                >
                  Download all ({media.length})
                </button>
              ) : null}

              {media.length === 0 ? (
                <p className="pma-modal-empty">
                  No images or videos available for this product.
                </p>
              ) : (
                <div className="pma-grid">
                  {media.map((singleMedia, index) => {
                    const progress = downloading[index];
                    return (
                      <div
                        className="pma-tile"
                        key={`${singleMedia.url}-${index}`}
                        onClick={() => this.downloadMedia(singleMedia, index)}
                      >
                        {singleMedia.type === "video" ? (
                          <div className="pma-video-tile">
                            {singleMedia.thumb ? (
                              <img
                                src={singleMedia.thumb}
                                alt="Product video"
                              />
                            ) : null}
                            <span className="pma-play-icon">
                              <svg viewBox="0 0 24 24" width="34" height="34">
                                <path fill="#ffffff" d="M8 5l11 7-11 7z" />
                              </svg>
                            </span>
                          </div>
                        ) : (
                          <img
                            src={singleMedia.url}
                            alt={`Product ${index + 1}`}
                          />
                        )}
                        {progress ? (
                          <div className="pma-progress-overlay">
                            <div className="pma-progress-percent">
                              {progress.percent === null
                                ? formatBytes(progress.loaded)
                                : `${progress.percent}%`}
                            </div>
                            <div className="pma-progress-track">
                              <div
                                className={`pma-progress-fill${
                                  progress.percent === null
                                    ? " pma-progress-indeterminate"
                                    : ""
                                }`}
                                style={
                                  progress.percent === null
                                    ? undefined
                                    : { width: `${progress.percent}%` }
                                }
                              />
                            </div>
                            {progress.total > 0 && (
                              <div className="pma-progress-size">
                                {formatBytes(progress.loaded)} /{" "}
                                {formatBytes(progress.total)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="pma-tile-download"
                            title="Download"
                            onClick={(event) => {
                              event.stopPropagation();
                              this.downloadMedia(singleMedia, index);
                            }}
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16">
                              <path
                                fill="none"
                                stroke="#555"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 3.5v10.5m0 0l-3.8-3.8M12 14l3.8-3.8M4.5 16.5v2a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-2"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ProductMediaActions;
